export interface TranscriptItem {
  transcript_id: string;
}

export interface TranscriptMetadata {
  transcript_id: string;
  created_at: string;
  created_by?: string | null;
}

export type SpeakerTone =
  | 'speakerA'
  | 'speakerB'
  | 'speakerC'
  | 'speakerD'
  | 'secondary';

export type TranscriptTextTone = 'primary' | 'secondary';

export interface TranscriptConversation<T extends TranscriptItem> {
  transcriptId: string;
  createdBy: string | null;
  items: T[];
}

export interface TranscriptConversationDisplay<T extends TranscriptItem>
  extends TranscriptConversation<T> {
  separatorTitle: string;
}

export const creatorToNormalizedCreator = (
  createdBy?: string | null,
): string | null => createdBy?.trim() || null;

export const transcriptsToMetadataById = (
  transcripts: TranscriptMetadata[],
): ReadonlyMap<string, TranscriptMetadata> => new Map(
  transcripts.map((transcript) => [transcript.transcript_id, transcript]),
);

export const transcriptsToOrderedIds = (
  transcripts: TranscriptMetadata[],
): string[] => transcripts
  .slice()
  .sort((left, right) => left.created_at.localeCompare(right.created_at))
  .map(({ transcript_id }) => transcript_id);

export const itemsToItemsByTranscriptId = <T extends TranscriptItem>(
  items: T[],
): ReadonlyMap<string, T[]> => items.reduce((itemsByTranscriptId, item) => {
  const transcriptItems = itemsByTranscriptId.get(item.transcript_id);

  if (transcriptItems) {
    transcriptItems.push(item);
  } else {
    itemsByTranscriptId.set(item.transcript_id, [item]);
  }

  return itemsByTranscriptId;
}, new Map<string, T[]>());

export const orderedTranscriptIdsWithItems =
  <T extends TranscriptItem>(
    itemsByTranscriptId: ReadonlyMap<string, T[]>,
  ) =>
  (orderedTranscriptIds: string[]): string[] =>
    orderedTranscriptIds.filter((transcriptId) =>
      itemsByTranscriptId.has(transcriptId),
    );

export const itemsByTranscriptIdToUnpersistedIds =
  (metadataByTranscriptId: ReadonlyMap<string, TranscriptMetadata>) =>
  <T extends TranscriptItem>(
    itemsByTranscriptId: ReadonlyMap<string, T[]>,
  ): string[] =>
    Array.from(itemsByTranscriptId.keys()).filter(
      (transcriptId) => !metadataByTranscriptId.has(transcriptId),
    );

export const transcriptIdsToConversationOrder =
  (persistedTranscriptIds: string[]) =>
  (unpersistedTranscriptIds: string[]): string[] => [
    ...persistedTranscriptIds,
    ...unpersistedTranscriptIds,
  ];

export const transcriptIdToConversation =
  (metadataByTranscriptId: ReadonlyMap<string, TranscriptMetadata>) =>
  <T extends TranscriptItem>(
    itemsByTranscriptId: ReadonlyMap<string, T[]>,
  ) =>
  (transcriptId: string): TranscriptConversation<T> => ({
    transcriptId,
    createdBy: creatorToNormalizedCreator(
      metadataByTranscriptId.get(transcriptId)?.created_by,
    ),
    items: itemsByTranscriptId.get(transcriptId) || [],
  });

export const transcriptsToConversations = (
  transcripts: TranscriptMetadata[],
) => {
  const metadataByTranscriptId = transcriptsToMetadataById(transcripts);
  const orderedTranscriptIds = transcriptsToOrderedIds(transcripts);

  return <T extends TranscriptItem>(
    items: T[],
  ): TranscriptConversation<T>[] => {
    const itemsByTranscriptId = itemsToItemsByTranscriptId(items);
    const persistedTranscriptIds = orderedTranscriptIdsWithItems(
      itemsByTranscriptId,
    )(orderedTranscriptIds);
    const unpersistedTranscriptIds = itemsByTranscriptIdToUnpersistedIds(
      metadataByTranscriptId,
    )(itemsByTranscriptId);
    const conversationOrder = transcriptIdsToConversationOrder(
      persistedTranscriptIds,
    )(unpersistedTranscriptIds);
    const conversationForTranscriptId = transcriptIdToConversation(
      metadataByTranscriptId,
    )(itemsByTranscriptId);

    return conversationOrder.map(conversationForTranscriptId);
  };
};

export const conversationsWithItemsByTranscriptId =
  <T extends TranscriptItem>(
    itemsByTranscriptId: ReadonlyMap<string, T[]>,
  ) =>
  <
    SourceItem extends TranscriptItem,
    Conversation extends TranscriptConversation<SourceItem>,
  >(
    conversations: Conversation[],
  ): Array<Omit<Conversation, 'items'> & { items: T[] }> =>
    conversations.map((conversation) => ({
      ...conversation,
      items: itemsByTranscriptId.get(conversation.transcriptId) || [],
    }));

export const conversationCreatorToTitle = (
  createdBy: string | null,
): string => createdBy
  ? `Conversation started by @${createdBy}`
  : 'Conversation started by an unknown user';

export const conversationToDisplayCreator =
  (activeTranscriptId: string | null) =>
  (activeConversationCreator: string | null) =>
  <T extends TranscriptItem>(
    conversation: TranscriptConversation<T>,
  ): string | null => {
    if (conversation.createdBy) {
      return conversation.createdBy;
    }

    return conversation.transcriptId === activeTranscriptId
      ? creatorToNormalizedCreator(activeConversationCreator)
      : null;
  };

export const conversationToDisplay =
  (activeTranscriptId: string | null) =>
  (activeConversationCreator: string | null) =>
  <T extends TranscriptItem>(
    conversation: TranscriptConversation<T>,
  ): TranscriptConversationDisplay<T> => {
    const createdBy = conversationToDisplayCreator(
      activeTranscriptId,
    )(activeConversationCreator)(conversation);

    return {
      ...conversation,
      separatorTitle: conversationCreatorToTitle(createdBy),
    };
  };

export const conversationsToDisplay =
  (activeTranscriptId: string | null) =>
  (activeConversationCreator: string | null) =>
  <T extends TranscriptItem>(
    conversations: TranscriptConversation<T>[],
  ): TranscriptConversationDisplay<T>[] => {
    const conversationForDisplay = conversationToDisplay(
      activeTranscriptId,
    )(activeConversationCreator);

    return conversations.map(conversationForDisplay);
  };

const speakerTones: Record<string, SpeakerTone> = {
  'Speaker A': 'speakerA',
  'Speaker B': 'speakerB',
  'Speaker C': 'speakerC',
  'Speaker D': 'speakerD',
};

export const speakerToTone = (
  speaker: string | null,
): SpeakerTone => speakerTones[speaker || ''] || 'secondary';

export const transcriptIdToTextTone = (
  transcriptId?: string,
): TranscriptTextTone =>
  !transcriptId || transcriptId === 'live'
    ? 'secondary'
    : 'primary';
